import { Component, OnInit } from '@angular/core';
import { Appointment } from '@modules/souscription/document-medicale/document-list/appointment';
import {
    ArmService,
    AuthenticationService,
    DossierMedicalService,
    EspaceClientService,
    EspaceDocumentaireService,
    GeolocalisationService,
    User,
} from '@kv-common-components/kx-services';
import { NGXLogger } from 'ngx-logger';
import { I18nPipe } from '@shared/pipes/i18n.pipe';
import { DocumentListHelperService } from '@shared/services/document-list-helper.service';
import { FormatHelperService } from '@shared/services/format-helper.service';
import { environment } from '@env/environment';
import { Router } from '@angular/router';
import { RouteEnum } from '@shared/services/route-enum';

@Component({
    selector: 'app-appointment-details',
    templateUrl: './appointment-details.component.html',
    styleUrls: ['./appointment-details.component.scss'],
})
export class AppointmentDetailsComponent implements OnInit {
    static readonly typologieBilanSanguin = 'BILAN SANGUIN COMPLET';
    static readonly typologieBilanBiologique = 'BILAN BIOLOGIQUE';
    static readonly iconUrlVert = 'assets/images/pin-vert.svg';
    static readonly iconUrlRose = 'assets/images/pin-rose.svg';

    appointment: Appointment;
    documentList = [];
    expectedMedicalDocs: any;
    currentUser: User;
    isLoading = false;
    coordinatesPraticien: google.maps.LatLngLiteral;
    iconUrlPraticien: string;
    geolocApiLoaded = false;
    showAppointmentCancelModal = false;

    constructor(
        protected logger: NGXLogger,
        private i18nPipe: I18nPipe,
        private espaceDocService: EspaceDocumentaireService,
        private authenticationService: AuthenticationService,
        private documentListHelper: DocumentListHelperService,
        public espCltService: EspaceClientService,
        private armService: ArmService,
        private dossierMedicalService: DossierMedicalService,
        private geolocService: GeolocalisationService,
        public router: Router
    ) {}

    ngOnInit(): void {
        this.isLoading = true;
        this.currentUser = this.authenticationService.currentUserValue;
        this.populateExpectedMedicalDocs();
        this.populateDocumentListAndAppointment();
        this.initGeolocApi();
    }

    populateExpectedMedicalDocs(): void {
        this.espaceDocService
            .getEspaceDocumentaireFromCache(
                this.currentUser.idPersonne,
                this.currentUser.idPropositionPrincipale
            )
            .subscribe((espaceDocumentaire) => {
                this.expectedMedicalDocs =
                    DocumentListHelperService.getLastDocumentForExpectedMedicalDocs(
                        espaceDocumentaire
                    );
            });
    }

    populateDocumentListAndAppointment(): void {
        const idPersonne = this.currentUser.idPersonne;
        this.espCltService
            .getEspaceClient(idPersonne)
            .subscribe((espaceClt) => {
                const idDossier = this.currentUser.idDossier.toString();
                const idAia = espaceClt.personne.idAia.toString();
                this.dossierMedicalService
                    .getIdDossierMedical(idAia, idDossier)
                    .subscribe((dataDossierMedical) => {
                        this.armService
                            .getDossierMedical(
                                dataDossierMedical.idDossierMedical
                            )
                            .subscribe((dossier) => {
                                this.armService
                                    .getTypesExamens()
                                    .subscribe((typesExamens) => {
                                        this.documentList =
                                            this.documentListHelper.getExpectedDocumentList(
                                                Number(
                                                    this.currentUser.idDossier
                                                ),
                                                espaceClt,
                                                typesExamens,
                                                dossier
                                            );
                                        this.populateAppointmentByIdRdv(
                                            dataDossierMedical.idDossierMedical,
                                            dossier.listeRendezVousMedical[0]
                                        );
                                    });
                            });
                    });
            });
    }

    populateAppointmentByIdRdv(idDossierMedical: string, idRdv: string): void {
        this.armService
            .getRendezVousMedical(idDossierMedical, idRdv)
            .subscribe((rdvArm) => {
                this.armService
                    .getPraticien(rdvArm.idApiPraticien)
                    .subscribe((praticienArm) => {
                        this.initAppointmentAndPraticien(
                            idDossierMedical,
                            rdvArm,
                            praticienArm
                        );
                    });
            });
    }

    initAppointmentAndPraticien(
        idDossierMedical: string,
        rdvArm: any,
        praticienArm: any
    ): void {
        this.appointment = DocumentListHelperService.createAppointmentFromArm(
            rdvArm,
            praticienArm
        );
        this.appointment.idDossierMedical = idDossierMedical;
        this.coordinatesPraticien = {
            lat: this.appointment.praticien.coordonneesGps.latitude,
            lng: this.appointment.praticien.coordonneesGps.longitude,
        };

        this.iconUrlPraticien = `${environment.assetsFolder}${AppointmentDetailsComponent.iconUrlRose}`;
        if (this.appointment.praticien.priseRendezVousEnLigne) {
            this.iconUrlPraticien = `${environment.assetsFolder}${AppointmentDetailsComponent.iconUrlVert}`;
        }

        this.isLoading = false;
    }

    initGeolocApi(): void {
        this.geolocService
            .loadGeolocApi(DocumentListHelperService.geolocApiUrl)
            .subscribe((loaded) => {
                this.geolocApiLoaded = loaded;
            });
    }

    //Retourne true si un bilan sanguin est present dans la liste des pieces medicales demandees
    isDocumentListContainBilanSanguin(): boolean {
        const bilanSanguin = this.documentList.find(
            (doc) =>
                doc.typologie ===
                AppointmentDetailsComponent.typologieBilanSanguin
        );
        return bilanSanguin !== undefined && bilanSanguin !== null;
    }

    //Permet de telecharger le document PIJ listant les pieces medicales attendues
    downloadFile() {
        this.espaceDocService
            .downloadDocument(
                this.currentUser.idPersonne,
                this.currentUser.idPropositionPrincipale,
                this.expectedMedicalDocs.idGed,
                this.expectedMedicalDocs.libelleNatureDocument
            )
            .subscribe(
                (success) => {
                    this.logger.debug(success);
                },
                (error) => {
                    this.logger.debug(error);
                }
            );
    }

    //Genere un flux ics invitation Outlook puis telecharge ce dernier
    addAppointmentToCalendar(): void {
        const title = this.i18nPipe
            .transform('appointment.appointmentconfirmation.ics.title')
            .replace('%s', this.appointment.praticien.libelleCentre);
        const appointmentDate = {
            start: FormatHelperService.getDateTimeIcsFormatted(
                this.appointment.dateDebut
            ),
            end: FormatHelperService.getDateTimeIcsFormatted(
                this.appointment.dateFin
            ),
        };

        // on replace les doubles espaces causés par des undefined possibles par espaces simples
        const location = [
            this.appointment.praticien.adresse1,
            this.appointment.praticien.adresse2,
            this.appointment.praticien.adresse3,
            this.appointment.praticien.codePostal,
            this.appointment.praticien.ville,
            this.appointment.praticien.pays,
        ]
            .join(' ')
            .replace('  ', ' ');

        const geo =
            this.appointment.praticien.coordonneesGps.latitude +
            ';' +
            this.appointment.praticien.coordonneesGps.longitude;
        let docList =
            this.i18nPipe.transform(
                'appointment.appointmentconfirmation.exam.text'
            ) + '\\n';
        for (const doc of this.documentList) {
            docList = docList + doc.label + '\\n';
        }

        const ordersList = [
            this.i18nPipe.transform(
                'appointment.appointmentconfirmation.exam.orders.title'
            ),
        ];
        const otherInfoList = [
            this.i18nPipe.transform(
                'appointment.appointmentconfirmation.exam.others.title'
            ),
            this.i18nPipe
                .transform(
                    'appointment.appointmentconfirmation.exam.others.details'
                )
                .split(/[\n]/g)
                .join('\\n'),
            this.i18nPipe.transform(this.getResultInfoTranslateKey()),
        ];

        const bilanSanguin = this.documentList.find(
            (doc) =>
                doc.typologie ===
                AppointmentDetailsComponent.typologieBilanSanguin
        );
        const bilanBiologique = this.documentList.find(
            (doc) =>
                doc.typologie ===
                AppointmentDetailsComponent.typologieBilanBiologique
        );

        if (bilanSanguin) {
            ordersList.push(
                this.i18nPipe.transform(
                    'appointment.appointmentconfirmation.exam.orders.firstdescription'
                )
            );
        }
        if (bilanBiologique) {
            ordersList.push(
                this.i18nPipe.transform(
                    'appointment.appointmentconfirmation.exam.orders.firstdescription.bio'
                )
            );
        }

        ordersList.push(
            this.i18nPipe
                .transform(
                    'appointment.appointmentconfirmation.event.description'
                )
                .split(/[\n]/g)
                .join('\\n')
        );
        const orders = ordersList.join('\\n');
        const otherInfo = otherInfoList.join('\\n');

        const ics = this.createIcs(title, appointmentDate, location, geo, [
            docList,
            orders,
            otherInfo,
        ]);
        window.open('data:text/calendar;charset=utf8,' + encodeURI(ics));
    }

    //Retourne une clef bundle selon la presence ou non d un document bilan sanguin ou biologique dans la liste des documents demandes
    getResultInfoTranslateKey(): string {
        let key =
            'appointment.appointmentconfirmation.exam.result.info.nocofrac';
        if (this.appointment.praticien.cofrac) {
            const bilanSanguin = this.documentList.find(
                (doc) =>
                    doc.typologie ===
                    AppointmentDetailsComponent.typologieBilanSanguin
            );
            const bilanBiologique = this.documentList.find(
                (doc) =>
                    doc.typologie ===
                    AppointmentDetailsComponent.typologieBilanBiologique
            );

            if (bilanSanguin) {
                key =
                    'appointment.appointmentconfirmation.exam.result.info.bilansanguin';
            } else if (bilanBiologique) {
                key =
                    'appointment.appointmentconfirmation.exam.result.info.bilanbio';
            } else {
                key =
                    'appointment.appointmentconfirmation.exam.result.info.cofrac';
            }
        }

        return key;
    }

    /**
     * Genere un flux ics pour proposer un rdv Outlook associe au rdv medical
     *
     * @param title
     * @param appointmentDate
     * @param location
     * @param geo
     * @param descriptionList
     */
    createIcs(title, appointmentDate, location, geo, descriptionList): string {
        const description = descriptionList.join('\\n\\n');
        const uid = DocumentListHelperService.guid();
        const icsBasicObject =
            'BEGIN:VCALENDAR\r\n' +
            'VERSION:2.0\r\n' +
            'METHOD:PUBLISH\r\n' +
            'PRODID:icalendar-caas\r\n' +
            'BEGIN:VEVENT\r\n' +
            'UID:' +
            uid +
            '\r\n' +
            'DTSTAMP:' +
            appointmentDate.start +
            '\r\n' +
            'DTSTART:' +
            appointmentDate.start +
            '\r\n' +
            'DTEND:' +
            appointmentDate.end +
            '\r\n' +
            'LOCATION:' +
            location +
            '\r\n' +
            'SUMMARY:' +
            title +
            '\r\n' +
            'GEO:' +
            geo +
            '\r\n' +
            'DESCRIPTION:' +
            description +
            '\r\n' +
            'END:VEVENT\r\n' +
            'END:VCALENDAR';

        return icsBasicObject;
    }

    goToEditRdvMedical() {
        this.router.navigateByUrl(
            `${RouteEnum.appointmentModify}/${this.appointment.praticien.idApi}`
        );
    }

    deleteAppointment(): void {
        this.armService
            .deleteRendezVousMedical(
                this.appointment.idDossierMedical,
                this.appointment.idRdvMedical
            )
            .subscribe(() => {
                this.router.navigateByUrl(RouteEnum.documentList);
            });
    }
}
